/**
 * @fileoverview Filters: [vdx-filters-form] with checkboxes, radios, and optional [vdx-filters-search] filter [vdx-filters-items] children by visibility.
 */

const MODULE = 'filters';

const SELECTOR_FORM = '[vdx-filters-form]';
const SELECTOR_ITEMS = '[vdx-filters-items]';
const SELECTOR_SEARCH = '[vdx-filters-search]';
const CLASS_CHECKED = 'vdx--checked';
const SEARCH_DEBOUNCE_MS = 500;

/** Attribute names for pairing. */
const ATTR_FORM = 'vdx-filters-form';
const ATTR_ITEMS = 'vdx-filters-items';

/**
 * Gets the attribute value as key; empty or missing becomes "".
 * @param {Element} el
 * @param {string} attr
 * @returns {string}
 */
function getKey(el, attr) {
    const v = el.getAttribute(attr);
    return v != null ? String(v).trim() : '';
}

/**
 * Returns the trimmed label text from the next sibling of the input (e.g. label text).
 * @param {Element} input
 * @returns {string}
 */
function getLabelForInput(input) {
    const next = input && input.nextElementSibling;
    const text = next && next.textContent != null ? next.textContent : '';
    return String(text).trim();
}

/**
 * Returns an array of lowercase label strings for all checked checkboxes in the form.
 * @param {HTMLInputElement[]} checkboxes
 * @returns {string[]}
 */
function getActiveCheckboxLabels(checkboxes) {
    return checkboxes
        .filter((cb) => cb.checked)
        .map(getLabelForInput)
        .filter(Boolean)
        .map((s) => s.toLowerCase());
}

/**
 * Returns the lowercase label of the checked radio, or null if none.
 * @param {HTMLInputElement[]} radios
 * @returns {string | null}
 */
function getActiveRadioLabel(radios) {
    const active = radios.find((r) => r.checked);
    if (!active) return null;
    const label = getLabelForInput(active);
    return label ? label.toLowerCase() : null;
}

/**
 * Applies visibility (display block/none) to each item based on checkbox labels, radio label, and search text.
 * @param {Element[]} listItems - Direct children of [vdx-filters-items].
 * @param {HTMLInputElement[]} checkboxes
 * @param {HTMLInputElement[]} radios
 * @param {HTMLInputElement | null} searchInput
 */
function applyFilters(listItems, checkboxes, radios, searchInput) {
    const activeCheckboxLabels = getActiveCheckboxLabels(checkboxes);
    const activeRadioLabel = getActiveRadioLabel(radios);
    const searchValue = searchInput && searchInput.value != null
        ? String(searchInput.value).trim().toLowerCase()
        : '';

    listItems.forEach((item) => {
        const text = (item.textContent != null ? item.textContent : '').toLowerCase();
        const matchesCheckboxes = activeCheckboxLabels.length === 0 || activeCheckboxLabels.some((label) => text.includes(label));
        const matchesRadio = !activeRadioLabel || text.includes(activeRadioLabel);
        const matchesSearch = searchValue === '' || text.includes(searchValue);
        const show = matchesCheckboxes && matchesRadio && matchesSearch;
        if (item.style) item.style.display = show ? 'block' : 'none';
    });
}

/**
 * Wires checkbox change listeners: toggles CLASS_CHECKED on parent label and reapplies filters.
 * @param {HTMLInputElement[]} checkboxes
 * @param {() => void} onFilter
 */
function wireCheckboxes(checkboxes, onFilter) {
    checkboxes.forEach((checkbox) => {
        checkbox.addEventListener('change', (event) => {
            const target = event.target;
            const labelEl = target && target.closest ? target.closest('label') : null;
            if (labelEl && labelEl.classList) {
                labelEl.classList.toggle(CLASS_CHECKED, !!target.checked);
            }
            onFilter();
        });
    });
}

/**
 * Wires radio change listeners: sets CLASS_CHECKED on the active radio's label and reapplies filters.
 * @param {HTMLInputElement[]} radios
 * @param {() => void} onFilter
 */
function wireRadios(radios, onFilter) {
    radios.forEach((radio) => {
        radio.addEventListener('change', (event) => {
            const target = event.target;
            radios.forEach((r) => {
                const labelEl = r.closest ? r.closest('label') : null;
                if (!labelEl || !labelEl.classList) return;
                labelEl.classList.toggle(CLASS_CHECKED, r === target && !!r.checked);
            });
            onFilter();
        });
    });
}

/**
 * Wires search input with debounce; on input, reapplies filters after SEARCH_DEBOUNCE_MS.
 * @param {HTMLInputElement | null} searchInput
 * @param {() => void} onFilter
 */
function wireSearch(searchInput, onFilter) {
    if (!searchInput || typeof searchInput.addEventListener !== 'function') return;
    let timer;
    searchInput.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(onFilter, SEARCH_DEBOUNCE_MS);
    });
}

/**
 * Builds form/items pairs by matching vdx-filters-form and vdx-filters-items keys.
 * Empty value pairs with empty value.
 * @returns {Array<{ form: Element, itemsContainer: Element }>}
 */
function getFilterPairs() {
    if (typeof document === 'undefined') return [];

    const forms = document.querySelectorAll(SELECTOR_FORM);
    const itemsContainers = document.querySelectorAll(SELECTOR_ITEMS);
    if (!forms.length || !itemsContainers.length) return [];

    /** @type {Map<string, { form: Element }>} */
    const formsByKey = new Map();
    forms.forEach((form) => {
        const key = getKey(form, ATTR_FORM);
        if (!formsByKey.has(key)) formsByKey.set(key, { form });
    });

    /** @type {Array<{ form: Element, itemsContainer: Element }>} */
    const pairs = [];
    itemsContainers.forEach((itemsContainer) => {
        const key = getKey(itemsContainer, ATTR_ITEMS);
        const entry = formsByKey.get(key);
        if (entry) pairs.push({ form: entry.form, itemsContainer });
    });
    return pairs;
}

/**
 * Initializes filters: pairs [vdx-filters-form] with [vdx-filters-items] by matching attribute value.
 * Empty value pairs with empty value. Form checkboxes/radios and optional [vdx-filters-search] filter items visibility.
 * Safe to call once (guarded by window.vdxFiltersInitialized).
 */
export function filterItems() {
    if (typeof document === 'undefined') return;
    if (window.vdxFiltersInitialized) return;

    const pairs = getFilterPairs();
    if (!pairs.length) return;

    pairs.forEach(({ form, itemsContainer }) => {
        const formInputs = form.querySelectorAll ? form.querySelectorAll('input') : [];
        const inputs = Array.from(formInputs);
        const checkboxes = inputs.filter((i) => i.type === 'checkbox');
        const radios = inputs.filter((i) => i.type === 'radio');
        if (!checkboxes.length && !radios.length) return;

        const searchInput = form.querySelector(SELECTOR_SEARCH);
        const runFilters = () => applyFilters(Array.from(itemsContainer.children), checkboxes, radios, searchInput);

        wireCheckboxes(checkboxes, runFilters);
        wireRadios(radios, runFilters);
        wireSearch(searchInput, runFilters);
        runFilters();
    });

    window.vdxFiltersInitialized = true;
}

/**
 * Reapplies filters to all [vdx-filters-items] containers using current DOM children.
 * Call after DOM changes (e.g. cmsMerge) so merged items are included in filtering.
 * Pairs form/items by matching attribute value; empty pairs with empty.
 * Safe to call anytime; no-op if no filter blocks exist.
 */
export function reapplyFilters() {
    const pairs = getFilterPairs();
    pairs.forEach(({ form, itemsContainer }) => {
        const formInputs = form.querySelectorAll ? form.querySelectorAll('input') : [];
        const inputs = Array.from(formInputs);
        const checkboxes = inputs.filter((i) => i.type === 'checkbox');
        const radios = inputs.filter((i) => i.type === 'radio');
        if (!checkboxes.length && !radios.length) return;

        const searchInput = form.querySelector(SELECTOR_SEARCH);
        const listItems = Array.from(itemsContainer.children);
        if (!listItems.length) return;

        applyFilters(listItems, checkboxes, radios, searchInput);
    });
}
